import type { CSSInterpolation } from '@emotion/serialize';
import type {
	InfiniteQueryObserverResult,
	QueryObserverResult,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import IconErrorAlt from '~icons/bx/error-alt';

import {
	ErrorIndicator,
	LoadingIndicator,
	SimpleEmptySection,
} from '@adflow/frontend/ui-graphics';
import { useDelayUnmount } from '@adflow/frontend/util-common';

import { handleRequestErrors } from './handle-request-errors';

export interface UseQueryStatusUIOptions {
	/** The message to display in an error toast if the queries fail. */
	errorMessage: ReactNode;

	/**
	 * When true ignore stale cached data and show loading UI until fresh data is fetched.
	 *
	 * This is should be used on components like edit forms because they use the first data
	 * they are given and then don't accept changes.
	 */
	waitUntilFetchedAfterMount?: boolean;
	/* Amount of time to wait before showing a loading indicator */
	loadingDelay?: number;

	/** When true show "Not found" message on a page and hide an error toast if the query fails with a 404. */
	showNotFoundIndicator?: boolean;

	/** The message to display if query fails with a 404. */
	notFoundError?: ReactNode;

	containerStyle?: CSSInterpolation;
}

type QueryResult =
	| InfiniteQueryObserverResult<unknown, unknown>
	| QueryObserverResult<unknown, unknown>;

/**
 * Handle repetitive logic of displaying UI for components that use `useQuery`.
 *
 * Returns a `JSX.Element` to display if any queries are in the loading or error states.
 * Returns `null` if all queries are loaded and ok.
 *
 * @example
 * function MyComponent(props) {
 *   const queryResult = useQuery(whatever);
 *   const antherQueryResult = useQuery(whatever);
 *
 *   const queryStatusIndicator = useQueryStatusUI(
 *     [queryResult, antherQueryResult],
 *     { errorMessage: 'Oh no! Could not load data.' }
 *   );
 *
 *   // One or more queries do not have their data ready.
 *   // Display a status indicator.
 *   if (queryStatusIndicator !== null) {
 *     return queryStatusIndicator;
 *   }
 *
 *   // All the data is ready. Now render the normal UI.
 *   return <div>{queryResult.data}</div>
 * }
 *
 * @param queryResults Array of return values of react-query's `useQuery`.
 */
export function useQueryStatusUI(
	queryResults: QueryResult[],
	options: UseQueryStatusUIOptions
): JSX.Element | null {
	const {
		errorMessage,
		loadingDelay,
		waitUntilFetchedAfterMount,
		showNotFoundIndicator,
		notFoundError,
	} = options;

	const { t } = useTranslation('common');

	const isLoading = queryResults.some((x) =>
		waitUntilFetchedAfterMount && x.isStale
			? !x.isFetchedAfterMount
			: x.isPending && x.fetchStatus === 'fetching'
	);

	const showIndicator = useDelayUnmount(!isLoading, loadingDelay ?? 0);

	const isError = queryResults.some((x) => x.isError);

	const isNotFoundError =
		isError &&
		queryResults
			.filter((x) => x.isError)
			.every((x) => isAxiosError(x.error) && x.error?.response?.status === 404);

	useEffect(() => {
		if (!isLoading && isError) {
			const errors = queryResults.map((res) => res.error);

			if (isNotFoundError && showNotFoundIndicator) {
				return;
			}

			handleRequestErrors(errors, { errorMessage });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isError, isLoading, errorMessage, isNotFoundError]);

	if (showIndicator && loadingDelay) {
		return null;
	}

	if (isLoading) {
		return <LoadingIndicator containerStyle={options.containerStyle} />;
	}

	if (isNotFoundError && showNotFoundIndicator) {
		return (
			<SimpleEmptySection
				Icon={IconErrorAlt}
				message={notFoundError ?? t('notFound')}
				containerStyle={options.containerStyle}
			/>
		);
	}

	if (isError) {
		return <ErrorIndicator containerStyle={options.containerStyle} />;
	}

	return null;
}
